import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { apiStatus } from 'shared/constants/AppEnum'
import { easebuzzSubmerchantAPI } from 'api/temple'
import { AppState } from 'store/reduxStore'
import { EasebuzzSubmerchantType } from 'types/api/temple'
import { IApiState } from 'store/types'
import { selectCurrentTempleId } from '../templeSlice'

const adapter = createEntityAdapter<EasebuzzSubmerchantType>()

type StateType = {
    listApi: IApiState,
    createApi: IApiState,
    createSubmerchantApi: IApiState,
    updateApi: IApiState,
    updateSubmerchantApi: IApiState,
    deleteApi: IApiState
}

const initialApi: StateType = {
    listApi: { status: apiStatus.IDLE, error: null },
    createApi: { status: apiStatus.IDLE, error: null },
    createSubmerchantApi: { status: apiStatus.IDLE, error: null },
    updateApi: { status: apiStatus.IDLE, error: null },
    updateSubmerchantApi: { status: apiStatus.IDLE, error: null },
    deleteApi: { status: apiStatus.IDLE, error: null },
}  

export const staffEasebuzzListThunk = createAsyncThunk(
    'staff/easebuzz/list',
    async (payload: any, { rejectWithValue, getState }) => {
        try {
              let templeId = selectCurrentTempleId(getState() as AppState)
            const response = await easebuzzSubmerchantAPI.list(templeId)
            // normalize server keys if needed
            return { data: response.data }
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffEasebuzzCreateThunk = createAsyncThunk(
    'staff/easebuzz/create',
    async (payload: Partial<EasebuzzSubmerchantType>, { rejectWithValue, getState }) => {
        try {
            let templeId = selectCurrentTempleId(getState() as AppState)
            const response = await easebuzzSubmerchantAPI.post(payload as any, templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffEasebuzzRetrieveThunk = createAsyncThunk(
    'staff/easebuzz/retrieve',
    async (payload: { url: string }, { rejectWithValue, getState }) => {
        try {
             let templeId = selectCurrentTempleId(getState() as AppState)
            const response = await easebuzzSubmerchantAPI.get(payload, templeId)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffEasebuzzUpdateThunk = createAsyncThunk(
    'staff/easebuzz/update',
    async (payload: { url: string, data: Partial<EasebuzzSubmerchantType> }, { rejectWithValue }) => {
        try {
            const response = await easebuzzSubmerchantAPI.update(payload.url, payload.data)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffEasebuzzDeleteThunk = createAsyncThunk(
    'staff/easebuzz/delete',
    async (payload: { url: string }, { rejectWithValue }) => {
        try {
            await easebuzzSubmerchantAPI.delete(payload.url)
            return payload.url
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffEasebuzzCreateSubmerchantThunk = createAsyncThunk(
    'staff/easebuzz/create-submerchant',
    async (payload: { id: string | number, sub_merchant_password: string, sub_merchant_confirm_password: string }, { rejectWithValue }) => {
        try {
            const response = await easebuzzSubmerchantAPI.createSubmerchant(payload.id, {
                sub_merchant_password: payload.sub_merchant_password,
                sub_merchant_confirm_password: payload.sub_merchant_confirm_password
            })
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

export const staffEasebuzzUpdateSubmerchantThunk = createAsyncThunk(
    'staff/easebuzz/update-submerchant',
    async (payload: { id: string | number }, { rejectWithValue }) => {
        try {
            const response = await easebuzzSubmerchantAPI.updateSubmerchant(payload.id)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data)
        }
    }
)

const slice = createSlice({
    name: 'easebuzzSubmerchant',
    initialState: adapter.getInitialState(initialApi),
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(staffEasebuzzListThunk.pending, (state) => {
            state.listApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(staffEasebuzzListThunk.fulfilled, (state, action) => {
            state.listApi = { status: apiStatus.SUCCEEDED, error: null }
            adapter.upsertMany(state, action.payload.data)
        })
        builder.addCase(staffEasebuzzListThunk.rejected, (state, action) => {
            state.listApi = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(staffEasebuzzCreateThunk.pending, (state) => {
            state.createApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(staffEasebuzzCreateThunk.fulfilled, (state, action) => {
            state.createApi = { status: apiStatus.SUCCEEDED, error: null }
            adapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffEasebuzzCreateThunk.rejected, (state, action) => {
            state.createApi = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(staffEasebuzzRetrieveThunk.fulfilled, (state, action) => {
            adapter.upsertOne(state, action.payload)
        })

        builder.addCase(staffEasebuzzUpdateThunk.fulfilled, (state, action) => {
            state.updateApi = { status: apiStatus.SUCCEEDED, error: null }
            adapter.upsertOne(state, action.payload)
        })

        builder.addCase(staffEasebuzzCreateSubmerchantThunk.pending, (state) => {
            state.createSubmerchantApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(staffEasebuzzCreateSubmerchantThunk.fulfilled, (state, action) => {
            state.createSubmerchantApi = { status: apiStatus.SUCCEEDED, error: null }
            // API returns updated submerchant info, upsert to keep state current
            adapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffEasebuzzCreateSubmerchantThunk.rejected, (state, action) => {
            state.createSubmerchantApi = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(staffEasebuzzUpdateSubmerchantThunk.pending, (state) => {
            state.updateSubmerchantApi = { status: apiStatus.LOADING, error: null }
        })
        builder.addCase(staffEasebuzzUpdateSubmerchantThunk.fulfilled, (state, action) => {
            state.updateSubmerchantApi = { status: apiStatus.SUCCEEDED, error: null }
            // API returns updated submerchant info, upsert to keep state current
            adapter.upsertOne(state, action.payload)
        })
        builder.addCase(staffEasebuzzUpdateSubmerchantThunk.rejected, (state, action) => {
            state.updateSubmerchantApi = { status: apiStatus.FAILED, error: action.payload }
        })

        builder.addCase(staffEasebuzzDeleteThunk.fulfilled, (state, action) => {
            adapter.removeOne(state, action.payload)
            state.deleteApi = { status: apiStatus.SUCCEEDED, error: null }
        })
    }
})

export const selectEasebuzzCreateSubmerchantApi = (state: AppState) => state.easebuzzSubmerchant.createSubmerchantApi
export const selectEasebuzzUpdateSubmerchantApi = (state: AppState) => state.easebuzzSubmerchant.updateSubmerchantApi

export default slice.reducer

export const {
    selectAll: selectAllEasebuzzSubmerchants,
    selectById: selectEasebuzzById,
} = adapter.getSelectors((state: AppState) => state.easebuzzSubmerchant)

export const selectEasebuzzListApi = (state: AppState) => state.easebuzzSubmerchant.listApi
export const selectEasebuzzCreateApi = (state: AppState) => state.easebuzzSubmerchant.createApi
export const selectEasebuzzUpdateApi = (state: AppState) => state.easebuzzSubmerchant.updateApi
export const selectEasebuzzDeleteApi = (state: AppState) => state.easebuzzSubmerchant.deleteApi
